import { useState } from 'react';
import { Modal } from '../../../components/Modal';
import Button from '../../../components/Button';
import { SpeechBubblePreview } from './SpeechBubblePreview';
import type { JourneyCardVariant, JourneySectionTemplateFirestore } from '../services/journeyTypes';

interface EditSectionModalProps {
    section: JourneySectionTemplateFirestore;
    onSave: (section: JourneySectionTemplateFirestore) => void;
    onCancel: () => void;
}

const COLOR_OPTIONS: JourneyCardVariant[] = ['yellow', 'blue', 'green', 'pink'];

export const EditSectionModal = ({ section, onSave, onCancel }: EditSectionModalProps) => {
    const [formData, setFormData] = useState<JourneySectionTemplateFirestore>({ ...section });
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (field: keyof JourneySectionTemplateFirestore, value: string | number) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        setIsSaving(true);
        try {
            await onSave(formData);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen onClose={onCancel} title={`Edit Section: ${section.header || 'Untitled'}`} size="md">
            <div className="space-y-4">
                <div>
                    <label htmlFor="section-header" className="block text-sm font-medium text-gray-700 mb-1">
                        Header *
                    </label>
                    <input
                        type="text"
                        id="section-header"
                        value={formData.header}
                        onChange={(e) => handleChange('header', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Section header"
                    />
                </div>

                <div>
                    <label htmlFor="section-ollieText" className="block text-sm font-medium text-gray-700 mb-1">
                        Ollie Text
                    </label>
                    <textarea
                        id="section-ollieText"
                        value={formData.ollieText}
                        onChange={(e) => handleChange('ollieText', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Ollie text"
                        rows={3}
                    />
                    {formData.ollieText && (
                        <div className="mt-2">
                            <span className="block text-xs text-gray-500 mb-1">Phone preview</span>
                            <div className="w-[375px] max-w-full mx-auto rounded-xl border border-gray-200 bg-osGrey-5 p-4 flex justify-center">
                                <SpeechBubblePreview text={formData.ollieText} />
                            </div>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="section-color" className="block text-sm font-medium text-gray-700 mb-1">
                            Color
                        </label>
                        <select
                            id="section-color"
                            value={formData.color}
                            onChange={(e) => handleChange('color', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {COLOR_OPTIONS.map((color) => (
                                <option key={color} value={color}>
                                    {color.charAt(0).toUpperCase() + color.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="section-order" className="block text-sm font-medium text-gray-700 mb-1">
                            Order
                        </label>
                        <input
                            type="number"
                            id="section-order"
                            value={formData.order}
                            onChange={(e) => handleChange('order', parseInt(e.target.value, 10) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            min={0}
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <Button onClick={onCancel} variant="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="primary" loading={isSaving} disabled={isSaving}>
                        Save
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
